import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@lognai/synapse-design-system-v2';
import { FormField } from '@lognai/plugin-components';
import { X } from '@lognai/synapse-design-system-v2/icons';
import {
  extractMentions,
  stripMentions,
  toMarkerText,
  type MentionedUser,
} from '@lognai/plugin-sdk';
import { LabelChips } from './LabelChips';
import { MentionTextarea } from '../mentions/MentionTextarea';
import { AssignFromMentionChip } from '../mentions/AssignFromMentionChip';
import type { CreateTaskDto, Task, AssignableUser, TaskPriority } from '../../types/tasks';
import { TASK_PRIORITIES, PRIORITY_LABELS } from '../../types/tasks';

const taskFormSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  description: z.string().optional(),
  priority: z.enum(['low', 'medium', 'high']),
  assigneeUserId: z.string().optional(),
  dueDate: z.string().optional(),
});

type TaskFormValues = z.infer<typeof taskFormSchema>;

/** Radix rejects an empty SelectItem value, so "no assignee" needs a sentinel. */
const UNASSIGNED = '__unassigned__';

export interface TaskFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: CreateTaskDto) => void;
  isLoading?: boolean;
  assignableUsers: AssignableUser[];
  initialData?: Task | null;
}

const toDateInput = (iso?: string) => (iso ? iso.slice(0, 10) : '');

export const TaskForm: React.FC<TaskFormProps> = ({
  open,
  onOpenChange,
  onSubmit,
  isLoading,
  assignableUsers,
  initialData,
}) => {
  const isEdit = !!initialData;
  const [labels, setLabels] = useState<string[]>([]);
  const [labelInput, setLabelInput] = useState('');
  const [viewerIds, setViewerIds] = useState<string[]>([]);
  // The description field holds DISPLAY text; these are the ids behind its @names, folded
  // back in as markers at submit so no user id ever reaches the textarea.
  const [mentionedUsers, setMentionedUsers] = useState<MentionedUser[]>([]);
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<TaskFormValues>({
    resolver: zodResolver(taskFormSchema),
    defaultValues: { priority: 'medium' },
  });

  useEffect(() => {
    if (open) {
      // A stored description is marker text. Editing it must show the user plain @names, so
      // convert back to display text and recover the ids alongside.
      const storedDescription = initialData?.description ?? '';

      reset({
        title: initialData?.title ?? '',
        description: stripMentions(storedDescription),
        priority: (initialData?.priority as TaskPriority) ?? 'medium',
        assigneeUserId: initialData?.assigneeUserId ?? '',
        dueDate: toDateInput(initialData?.dueDate),
      });
      setMentionedUsers(extractMentions(storedDescription));
      setLabels(initialData?.labels ?? []);
      setLabelInput('');
      setViewerIds(initialData?.viewerUserIds ?? []);
    }
  }, [open, initialData, reset]);

  const addLabel = () => {
    const v = labelInput.trim();
    if (v && !labels.includes(v)) setLabels((prev) => [...prev, v]);
    setLabelInput('');
  };

  const assigneeId = watch('assigneeUserId');
  const viewerOptions = assignableUsers.filter(
    (u) => u.id !== assigneeId && !viewerIds.includes(u.id)
  );
  const nameForUser = (uid: string) => {
    const u = assignableUsers.find((x) => x.id === uid);
    return u?.fullName || u?.email || uid;
  };

  const submit = (values: TaskFormValues) => {
    // The backend reads `''` as an explicit clear and `undefined` as "unchanged",
    // so an emptied assignee/due date must go over the wire as an empty string.
    onSubmit({
      title: values.title,
      // Display text -> marker text. The server re-parses the markers to derive who gains
      // access, so the id stays the authority even though the user never typed one.
      description: values.description
        ? toMarkerText(values.description, mentionedUsers)
        : undefined,
      priority: values.priority,
      assigneeUserId: values.assigneeUserId ?? '',
      dueDate: values.dueDate ?? '',
      labels,
      viewerUserIds: viewerIds,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Task' : 'Create New Task'}</DialogTitle>
          <DialogDescription>Fill in the details for the task.</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(submit)} className="space-y-4">
          <FormField label="Title" required error={errors.title?.message}>
            <Input {...register('title')} placeholder="Task title" />
          </FormField>

          <FormField label="Description (Optional)">
            <MentionTextarea
              value={watch('description') ?? ''}
              onChange={(next) => setValue('description', next, { shouldDirty: true })}
              mentionedUsers={mentionedUsers}
              onMentionedUsersChange={setMentionedUsers}
              rows={3}
              placeholder="Add more detail, or type @ to bring someone in…"
            />
          </FormField>

          {/* Writes the same `assigneeUserId` the picker below writes — a mention
              alone never assigns. */}
          <AssignFromMentionChip
            mentionedUsers={mentionedUsers}
            currentAssigneeUserId={assigneeId || undefined}
            onAssign={(mentionedUser) =>
              setValue('assigneeUserId', mentionedUser.userId, { shouldDirty: true })
            }
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField label="Priority">
              <Select
                value={watch('priority')}
                onValueChange={(v) => setValue('priority', v as TaskPriority)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TASK_PRIORITIES.map((p) => (
                    <SelectItem key={p} value={p}>
                      {PRIORITY_LABELS[p]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>

            <FormField label="Assign To">
              <Select
                value={watch('assigneeUserId') || UNASSIGNED}
                onValueChange={(v) => setValue('assigneeUserId', v === UNASSIGNED ? '' : v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select an assignee…" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={UNASSIGNED}>Unassigned</SelectItem>
                  {assignableUsers.map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.fullName || u.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
          </div>

          <FormField label="Due Date">
            <div className="flex items-center gap-2">
              <Input type="date" {...register('dueDate')} />
              {watch('dueDate') && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setValue('dueDate', '')}
                >
                  Clear
                </Button>
              )}
            </div>
          </FormField>

          <FormField label="Labels">
            <div className="space-y-2">
              <Input
                value={labelInput}
                onChange={(e) => setLabelInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ',') {
                    e.preventDefault();
                    addLabel();
                  }
                }}
                onBlur={addLabel}
                placeholder="Type a label and press Enter"
              />
              <LabelChips
                labels={labels}
                onRemove={(label) => setLabels((prev) => prev.filter((l) => l !== label))}
              />
            </div>
          </FormField>

          <FormField label="Who can see this (optional)">
            <div className="space-y-2">
              <Select
                key={viewerIds.length}
                onValueChange={(v) =>
                  setViewerIds((prev) => (prev.includes(v) ? prev : [...prev, v]))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Add a viewer…" />
                </SelectTrigger>
                <SelectContent>
                  {viewerOptions.length === 0 ? (
                    <div className="px-2 py-1.5 text-sm text-muted-foreground">No more users</div>
                  ) : (
                    viewerOptions.map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.fullName || u.email}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              {viewerIds.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {viewerIds.map((uid) => (
                    <span
                      key={uid}
                      className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs"
                    >
                      {nameForUser(uid)}
                      <button
                        type="button"
                        onClick={() => setViewerIds((prev) => prev.filter((x) => x !== uid))}
                        aria-label={`Remove ${nameForUser(uid)}`}
                        className="hover:opacity-70"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                The assignee and you can always see this task. Add anyone else who needs access.
              </p>
            </div>
          </FormField>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isEdit ? 'Save Changes' : 'Create Task'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
